/**
 * AiReviewBatchListDrawer — 批量审查记录列表（波次 2）。
 *
 * 挂在文档库（数据库页）工具栏：批量审查从文档库发起，进度与历史回看留在
 * 发起处闭环最短；工作流页是模板/实例编排域，批量文档审查不是工作流实例，
 * 挂彼处会造成域混淆。当前选中事项时按 matterId 过滤（服务端过滤），
 * 全库视图列出跨事项的全部批次。
 */

import { useState } from "react";
import { Button, Drawer, List, Pagination, Space, Tag, Typography } from "antd";
import { useAiReviewBatches } from "../../api/queries";
import {
  AI_REVIEW_BATCH_STATUS_LABELS,
} from "../../types/aiReviewBatch";
import { formatDateTime } from "../../utils/date";
import { BATCH_STATUS_TAG_COLOR } from "./constants";
import EmptyState from "../common/EmptyState";
import { SkeletonGrid } from "../common/Skeleton";

const { Text } = Typography;
const PAGE_SIZE = 20;

interface AiReviewBatchListDrawerProps {
  open: boolean;
  onClose: () => void;
  /** 当前文档库选中的事项；null/undefined = 跨事项全量。 */
  matterId?: string | null;
  /** 点击一条记录 → 打开批详情抽屉。 */
  onSelectBatch: (batchId: string) => void;
}

export default function AiReviewBatchListDrawer({
  open,
  onClose,
  matterId,
  onSelectBatch,
}: AiReviewBatchListDrawerProps) {
  const [page, setPage] = useState(1);
  const query = useAiReviewBatches(
    open ? { matterId: matterId ?? undefined, page, pageSize: PAGE_SIZE } : undefined,
  );
  const batches = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  return (
    <Drawer
      title="批量审查记录"
      open={open}
      onClose={onClose}
      width={560}
      destroyOnHidden
      data-testid="batch-list-drawer"
    >
      {query.isLoading ? (
        <SkeletonGrid count={5} />
      ) : query.isError ? (
        <EmptyState
          title="加载失败"
          description="无法获取批量审查记录，请稍后重试。"
          action={
            <Button type="primary" onClick={() => query.refetch()}>
              重试
            </Button>
          }
        />
      ) : (
        <div>
          <List
            dataSource={batches}
            locale={{ emptyText: "还没有批量审查记录。在文档库勾选文档后发起。" }}
            renderItem={(batch) => (
              <List.Item
                key={batch.id}
                data-testid={`batch-list-row-${batch.id}`}
                actions={[
                  <Button
                    key="open"
                    type="link"
                    size="small"
                    onClick={() => onSelectBatch(batch.id)}
                  >
                    查看
                  </Button>,
                ]}
              >
                <Space direction="vertical" size={2}>
                  <Space size="small" wrap>
                    <Tag color={BATCH_STATUS_TAG_COLOR[batch.status]}>
                      {AI_REVIEW_BATCH_STATUS_LABELS[batch.status]}
                    </Tag>
                    <span>{batch.title ?? `批量审查（${batch.documentIds.length} 份）`}</span>
                  </Space>
                  <Text type="secondary">
                    {batch.documentIds.length} 份文档
                    {batch.createdAt ? ` · ${formatDateTime(batch.createdAt)}` : ""}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
          {total > PAGE_SIZE && (
            <div style={{ marginTop: "var(--jerry-sp-3)", textAlign: "right" }}>
              <Pagination
                size="small"
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                showSizeChanger={false}
                onChange={setPage}
                simple
              />
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
